// src/pages/settings/components/SecuritySettings.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  SecurityOutlined as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as PasswordIcon,
  PhoneAndroid as PhoneIcon,
  LockOutlined as LockIcon,
  Apps as AppsIcon,
  History as HistoryIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

// Import stores
import useAuthStore from '../../../stores/authStore';
import useNotificationStore from '../../../stores/notificationStore';

const SecuritySettings = () => {
  const theme = useTheme();
  const { user, updatePassword, setupTwoFactor, isLoading } = useAuthStore();
  const { showSuccess, showError } = useNotificationStore();

  // Form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.twoFactorEnabled || false);
  const [stayLoggedIn, setStayLoggedIn] = useState(user?.stayLoggedIn || false);
  const [sessionTimeout, setSessionTimeout] = useState(user?.sessionTimeout || 30);
  
  // Dialog state
  const [twoFactorDialogOpen, setTwoFactorDialogOpen] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorQrCode, setTwoFactorQrCode] = useState('');
  const [twoFactorSecret, setTwoFactorSecret] = useState('');

  // Form validation
  const [errors, setErrors] = useState({});

  // Handle password form change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({
      ...passwordForm,
      [name]: value,
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = (field) => {
    switch (field) {
      case 'currentPassword':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'newPassword':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirmPassword':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  // Toggle two-factor authentication
  const handleToggleTwoFactor = async () => {
    if (!twoFactorEnabled) {
      // If enabling 2FA, show setup dialog
      try {
        // In a real app, this would make an API call to get the QR code and secret
        // For demo purposes, we're simulating it
        setTwoFactorDialogOpen(true);
        setTwoFactorQrCode('https://example.com/qrcode');
        setTwoFactorSecret('ABC123DEF456');
      } catch (error) {
        showError('Failed to initialize two-factor authentication');
      }
    } else {
      // If disabling 2FA, show confirmation dialog
      if (window.confirm('Are you sure you want to disable two-factor authentication? This will reduce the security of your account.')) {
        try {
          // In a real app, this would make an API call to disable 2FA
          await setupTwoFactor(false);
          setTwoFactorEnabled(false);
          showSuccess('Two-factor authentication disabled');
        } catch (error) {
          showError('Failed to disable two-factor authentication');
        }
      }
    }
  };

  // Handle session timeout change
  const handleSessionTimeoutChange = (e) => {
    setSessionTimeout(Number(e.target.value));
  };

  // Handle stay logged in toggle
  const handleStayLoggedInToggle = (e) => {
    setStayLoggedIn(e.target.checked);
  };

  // Validate password form
  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle password form submit
  const handleChangePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }
    
    try {
      await updatePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );
      
      showSuccess('Password changed successfully');
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      showError(error.message || 'Failed to change password');
    }
  };

  // Handle 2FA setup confirmation
  const handleConfirmTwoFactorSetup = async () => {
    try {
      // In a real app, this would verify the code with the API
      if (twoFactorCode === '123456') { // Simulating verification
        await setupTwoFactor(true);
        setTwoFactorEnabled(true);
        setTwoFactorDialogOpen(false);
        showSuccess('Two-factor authentication enabled');
      } else {
        showError('Invalid verification code');
      }
    } catch (error) {
      showError('Failed to verify code');
    }
  };

  // Save session settings
  const handleSaveSessionSettings = () => {
    try {
      // In a real app, this would make an API call
      // For now, just show success message
      showSuccess('Session settings saved successfully');
    } catch (error) {
      showError('Failed to save session settings');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Security Settings</Typography>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        {/* Change Password */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PasswordIcon sx={{ mr: 1 }} />
              Change Password
            </Typography>
            
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="Current Password"
                name="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleTogglePasswordVisibility('currentPassword')}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="New Password"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                error={!!errors.newPassword}
                helperText={errors.newPassword || 'Password must be at least 8 characters'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleTogglePasswordVisibility('newPassword')}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Confirm New Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleTogglePasswordVisibility('confirmPassword')}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                variant="contained"
                
                onClick={handleChangePassword}
                disabled={isLoading}
                sx={{ mt: 2 }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* Two-Factor Authentication */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ mr: 1 }} />
              Two-Factor Authentication
            </Typography>
            
            <Typography variant="body2" paragraph sx={{ mt: 1 }}>
              Two-factor authentication adds an extra layer of security to your account by requiring a code from your mobile device in addition to your password.
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={twoFactorEnabled}
                    onChange={handleToggleTwoFactor}
                    
                  />
                }
                label={twoFactorEnabled ? 'Enabled' : 'Disabled'}
              />
              
              <Box sx={{ ml: 2 }}>
                {twoFactorEnabled ? (
                  <Alert severity="success" icon={false} sx={{ py: 0 }}>
                    Your account is protected
                  </Alert>
                ) : (
                  <Alert severity="warning" icon={false} sx={{ py: 0 }}>
                    We recommend enabling 2FA
                  </Alert>
                )}
              </Box>
            </Box>
          </Paper>
          
          {/* Session Settings */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ mr: 1 }} />
              Session Settings
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={stayLoggedIn}
                  onChange={handleStayLoggedInToggle}
                  
                />
              }
              label="Stay logged in"
            />
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2, ml: 4 }}>
              Keep me logged in on this device (not recommended for shared devices)
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Session Timeout (minutes)
              </Typography>
              <TextField
                select
                value={sessionTimeout}
                onChange={handleSessionTimeoutChange}
                variant="outlined"
                SelectProps={{
                  native: true,
                }}
                sx={{ width: '200px' }}
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={240}>4 hours</option>
              </TextField>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                Your session will expire after this period of inactivity
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              
              startIcon={<SaveIcon />}
              onClick={handleSaveSessionSettings}
              sx={{ mt: 2 }}
            >
              Save Session Settings
            </Button>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Two-Factor Authentication Setup Dialog */}
      <Dialog
        open={twoFactorDialogOpen}
        onClose={() => setTwoFactorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            Scan this QR code with your authenticator app (such as Google Authenticator, Authy, or Microsoft Authenticator).
          </DialogContentText>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            {/* This would be an actual QR code in a real app */}
            <Box
              sx={{
                width: 200,
                height: 200,
                border: '1px solid #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f5f5',
              }}
            >
              <Typography>QR Code Placeholder</Typography>
            </Box>
          </Box>
          
          <DialogContentText paragraph>
            If you can't scan the QR code, enter this code manually in your app:
          </DialogContentText>
          
          <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 2, borderRadius: 1, mb: 3 }}>
            <Typography variant="h6" align="center" fontFamily="monospace">
              {twoFactorSecret}
            </Typography>
          </Box>
          
          <DialogContentText>
            Enter the 6-digit verification code from your authenticator app:
          </DialogContentText>
          
          <TextField
            autoFocus
            margin="dense"
            label="Verification Code"
            fullWidth
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value)}
            inputProps={{ maxLength: 6 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFactorDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmTwoFactorSetup}
            
            variant="contained"
            disabled={twoFactorCode.length !== 6}
          >
            Verify and Enable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecuritySettings;